import { queryOptions, useQuery } from '@tanstack/react-query';

import { KentGetClusterInstallScriptRequest } from '@cast/types-kent';

import { apiClient } from '@cast/infrastructure-api';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

const KENT_CLUSTER_INSTALL_SCRIPT_QUERY_KEY = 'kent-cluster-install-script';

const getClusterInstallScriptQueryKey = (
  organizationId?: string,
  clusterId?: string,
  installScriptOptions?: KentGetClusterInstallScriptRequest['installScriptOptions']
) =>
  [
    KENT_CLUSTER_INSTALL_SCRIPT_QUERY_KEY,
    organizationId,
    clusterId,
    installScriptOptions,
  ] as const;

const getClusterInstallScriptQueryOptions = ({
  organizationId,
  clusterId,
  installScriptOptions,
  enabled = true,
}: {
  organizationId?: string;
  clusterId: string;
  installScriptOptions: KentGetClusterInstallScriptRequest['installScriptOptions'];
  enabled?: boolean;
}) =>
  queryOptions({
    queryKey: getClusterInstallScriptQueryKey(
      organizationId,
      clusterId,
      installScriptOptions
    ),
    queryFn: async () => {
      const { data } = await apiClient.kent.getClusterInstallScript({
        clusterId,
        organizationId: organizationId ?? '',
        installScriptOptions,
      });

      // TODO:(Daniel): remove casting once API is updated
      // https://castai.atlassian.net/browse/UI-5395
      return (data as unknown as string) || '';
    },
    enabled: enabled && !!organizationId,
  });

type Params = {
  requestData: Omit<KentGetClusterInstallScriptRequest, 'organizationId'>;
  enabled?: boolean;
};

export const useGetClusterInstallScriptQuery = ({
  requestData: { clusterId, installScriptOptions },
  enabled = true,
}: Params) => {
  const { currentOrganization } = useOrganizations();

  return useQuery(
    getClusterInstallScriptQueryOptions({
      organizationId: currentOrganization?.id,
      clusterId,
      installScriptOptions,
      enabled,
    })
  );
};
